import React, { useEffect, useMemo } from 'react'
import { Copy, ExternalLink, X } from 'lucide-react'
import styles from './SegmentCompareModal.module.css'
import { sideBySideDiff } from './promptAnalyzer'
import { copyText } from './clipboard'
import { DiffToken, SegmentCompareModalProps } from './types'

function DiffText({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.map((t, i) => {
        if (t.type === 'removed') return <del key={i}>{t.text}</del>
        if (t.type === 'added') return <ins key={i}>{t.text}</ins>
        return <React.Fragment key={i}>{t.text}</React.Fragment>
      })}
    </>
  )
}

function Column({ title, text, children }: { title: string; text: string; children: React.ReactNode }) {
  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <span className={styles.columnTitle}>{title}</span>
        <button type="button" className={styles.copyBtn} onClick={() => copyText(text)}>
          <Copy size={13} /> Copy
        </button>
      </div>
      <pre className={styles.columnBody}>{children}</pre>
    </div>
  )
}

const SegmentCompareModal: React.FC<SegmentCompareModalProps> = ({ segment, sourceContent, onClose, onJump }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const analyzedText = segment.text.trim()
  const source = sourceContent?.trim() ?? ''
  const diff = useMemo(
    () => (segment.kind === 'similar' ? sideBySideDiff(source, analyzedText) : null),
    [segment.kind, source, analyzedText],
  )

  const title =
    segment.kind === 'unknown' ? 'Not in any prompt'
      : segment.kind === 'match' ? `Identical to "${segment.label}"`
        : `Similar to "${segment.label}" (${Math.round(segment.similarity * 100)}%)`

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 className={`${styles.title} ${styles[segment.kind]}`}>{title}</h3>
          <div className={styles.headerActions}>
            {segment.kind !== 'unknown' && (
              <button type="button" className={styles.goBtn} onClick={() => { onClose(); onJump(segment.key) }}>
                <ExternalLink size={14} /> Go to prompt
              </button>
            )}
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {segment.kind === 'similar' ? (
          <>
            <div className={styles.legend}>
              <span><del>removed</del> = in the saved prompt, missing from the analyzed text</span>
              <span><ins>added</ins> = in the analyzed text, not in the saved prompt</span>
            </div>
            <div className={styles.sideBySide}>
              <Column title="Saved prompt (source)" text={source}>
                {diff ? <DiffText tokens={diff.left} /> : source}
              </Column>
              <Column title="Analyzed prompt" text={analyzedText}>
                {diff ? <DiffText tokens={diff.right} /> : analyzedText}
              </Column>
            </div>
            {!diff && <p className={styles.note}>Too large to compare word by word - showing both texts as-is.</p>}
          </>
        ) : (
          <div className={styles.single}>
            <Column title={segment.kind === 'match' ? 'Same in saved and analyzed prompt' : 'Analyzed prompt'} text={analyzedText}>
              {analyzedText}
            </Column>
          </div>
        )}
      </div>
    </div>
  )
}

export default SegmentCompareModal
